import { apiPost } from './api';
import { decompressData } from './compression';

/**
 * Normalize aggregation records to match the format expected by existing analytics code.
 * Maps gross_gaming_revenue → ggr, net_gaming_revenue → ngr, etc.
 */
export const normalizeAggregationRecord = (record) => {
  return {
    ...record,
    ggr: record.gross_gaming_revenue ?? record.ggr ?? 0,
    ngr: record.net_gaming_revenue ?? record.ngr ?? 0,
    turnover: record.turnover ?? record.total_bet_amount ?? 0,
  };
};

/**
 * Fetch pre-calculated aggregations for a list of entity usernames
 * (countries, cities, super-agents, agents, or players).
 * Uses the same /analytics API since all records share the same table.
 */
export const fetchParentAggregations = async (entityUserIds, startDate, endDate, currency, token) => {
  if (!entityUserIds || entityUserIds.length === 0) {
    return { data: [], found: false };
  }

  try {
    console.log(`[Aggregations] Fetching for ${entityUserIds.length} direct reports`);

    const response = await apiPost(
      'analytics',
      '/analytics',
      {
        userIds: entityUserIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        currency,
        aggregate: false
      },
      token
    );

    let data = [];
    if (response.compressed && response.data) {
      const decompressedResponse = decompressData(response.data);
      if (decompressedResponse.success && decompressedResponse.data) {
        data = decompressedResponse.data;
      }
    } else if (response.success && response.data) {
      data = response.data;
    }

    if (data.length > 0) {
      console.log(`[Aggregations] Got ${data.length} records for direct reports`);
      const normalizedRecords = data.map(normalizeAggregationRecord);
      return { data: normalizedRecords, found: true };
    }

    console.log('[Aggregations] No records returned for direct reports');
    return { data: [], found: false };
  } catch (error) {
    console.warn('[Aggregations] Failed to fetch aggregations:', error.message);
    return { data: [], found: false };
  }
};

/**
 * Fetch aggregations for direct reports of a selected entity.
 *
 * @param {object[]} directReports - Direct report nodes (with username)
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {string} currency
 * @param {string} token
 * @returns {{ data: object[], found: boolean, entityToReportMapping: Map<string,string> }}
 */
export const tryAggregationsWithFallback = async (directReports, startDate, endDate, currency, token) => {
  if (!directReports || directReports.length === 0) {
    return { data: [], found: false, entityToReportMapping: new Map() };
  }

  const directReportUsernames = directReports.map(r => r.username);

  const aggResult = await fetchParentAggregations(
    directReportUsernames, startDate, endDate, currency, token
  );

  const entityToReportMapping = new Map();
  directReportUsernames.forEach(u => entityToReportMapping.set(u, u));

  if (aggResult.found) {
    return { ...aggResult, entityToReportMapping };
  }

  return { data: [], found: false, entityToReportMapping: new Map() };
};
